import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useCookies } from 'react-cookie';
import { API_BASE_URL } from '../config';
import LoadingSpinner from '../components/LoadingSpinner';
import '../styles/auth.css'; // Re-use some auth form styles
import { sanitizeRedirectPath } from '../utils/googleAuth';

const CompleteGoogleRegistration = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [, setCookie] = useCookies(['access_token']);

    const [userData, setUserData] = useState(null);
    const [formData, setFormData] = useState({
        username: '',
        lastName: ''
    });
    const [acceptTerms, setAcceptTerms] = useState(false);
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [postAuthRedirect, setPostAuthRedirect] = useState(null);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        setPostAuthRedirect(
            sanitizeRedirectPath(params.get('redirect'))
            || sanitizeRedirectPath(sessionStorage.getItem('googleAuthRedirect'))
        );
        const data = params.get('data');
        if (data) {
            try {
                const decodedData = JSON.parse(atob(data));
                setUserData(decodedData);
            } catch (err) {
                setError('Invalid registration data. Please try signing up again.');
            }
        } else {
            setError('Missing registration data. Please try signing up again.');
        }
    }, [location]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);

        // Validate terms acceptance
        if (!acceptTerms) {
            setError('You must accept the Terms and Conditions to proceed');
            setIsLoading(false);
            return;
        }

        try {
            const response = await axios.post(`${API_BASE_URL}/auth/google/complete-registration`, {
                ...userData,
                username: formData.username,
                lastName: formData.lastName
            });

            const { token, userID, username, isPowerUser } = response.data;
            
            // 1. Save token and user ID with 7-day persistence
            const hostname = window.location.hostname;
            const isProduction = hostname !== 'localhost' && hostname !== '127.0.0.1';
            
            setCookie('access_token', token, {
                path: '/',
                maxAge: 7 * 24 * 60 * 60, // 7 days in seconds
                sameSite: 'lax',
                secure: isProduction,
                ...(isProduction && hostname.includes('.') && { domain: hostname.startsWith('www.') ? hostname.substring(4) : hostname })
            });
            
            localStorage.setItem('userID', userID);
            localStorage.setItem('auth_token', token);
            if (username) localStorage.setItem('username', username);
            localStorage.setItem('isPowerUser', isPowerUser ? 'true' : 'false');

            // 2. Notify app of auth update
            window.dispatchEvent(new Event('auth-token-updated'));

            // 3. Return to MCP flow or app home
            sessionStorage.removeItem('googleAuthRedirect');
            navigate(postAuthRedirect || '/searchrecipes', { replace: true });

        } catch (err) {
            setError(err.response?.data?.message || 'Failed to complete registration. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    if (!userData && !error) {
        return (
            <div className="auth-form">
                <LoadingSpinner text="Loading your info..." />
            </div>
        );
    }

    return (
        <div className="auth">
            <div className="auth-container">
                <div className="auth-form">
                    <h2>Complete Your Profile</h2>
                    {error && <div className="error-message">{error}</div>}
                    
                    <p style={{ 
                        color: '#6b7280', 
                        fontSize: window.innerWidth <= 768 ? '0.9rem' : '1rem',
                        lineHeight: '1.5',
                        marginBottom: '1.5rem'
                    }}>
                        Welcome! We've got your details from Google. Just a couple more things to get you set up.
                    </p>
                    
                    <form onSubmit={handleSubmit}>
                        <div className="form-group">
                            <label>Email</label>
                            <input type="email" value={userData?.email || ''} disabled />
                        </div>

                        <div className="form-group">
                            <label>First Name</label>
                            <input type="text" value={userData?.firstName || ''} disabled />
                        </div>
                        
                        <div className="form-group">
                            <label htmlFor="username">Username</label>
                            <input
                                type="text"
                                id="username"
                                name="username"
                                value={formData.username}
                                onChange={handleChange}
                                required
                                disabled={isLoading}
                                placeholder="Choose a unique username"
                            />
                            <small style={{ 
                                color: '#6b7280', 
                                fontSize: '0.8rem',
                                marginTop: '0.25rem'
                            }}>
                                This is your unique name on the platform.
                            </small>
                        </div>
                        
                        <div className="form-group">
                            <label htmlFor="lastName">Last Name</label>
                            <input
                                type="text"
                                id="lastName"
                                name="lastName"
                                value={formData.lastName}
                                onChange={handleChange}
                                required
                                disabled={isLoading}
                                placeholder="Enter your last name"
                            />
                        </div>
                        
                        {/* Terms and Conditions Checkbox */}
                        <div className="form-group terms-checkbox-group">
                            <label className="terms-checkbox-label">
                                <input
                                    type="checkbox"
                                    checked={acceptTerms}
                                    onChange={(e) => setAcceptTerms(e.target.checked)}
                                    disabled={isLoading}
                                    required
                                    className="terms-checkbox"
                                />
                                <span className="terms-text">
                                    I agree to the{' '}
                                    <Link 
                                        to="/terms-and-conditions" 
                                        target="_blank" 
                                        className="terms-link"
                                    >
                                        Terms and Conditions
                                    </Link>
                                    {' '}and understand that my content may be used by WhatToMake.co.za
                                </span>
                            </label>
                        </div>
                        
                        <button type="submit" disabled={isLoading || !acceptTerms}>
                            {isLoading ? (
                                <div className="flex items-center justify-center">
                                    <LoadingSpinner size="sm" color="primary" text="" />
                                    <span className="ml-2">Saving...</span>
                                </div>
                            ) : 'Complete Registration'}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default CompleteGoogleRegistration;
